import React, { useState } from 'react'
import styled from 'styled-components'
import { Small, Subtitle } from 'components/styled/typography'
import { connect } from 'react-redux'
import { Modal } from 'components/styled/modal'
import { Box } from 'components/styled/containers/box'
import { Button } from 'components/styled/button'
import { I18nComponentType } from 'i18n/i18n-types'
import { mapLanguageToComponentProps } from 'i18n'
import { BodyStyle } from 'components/styled/typography/styles'
import { VisiblyHidden } from 'theme/accessibility'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import { RatingStar, RatingStars } from 'components/styled/rating/rating-stars'
import {
  BaseButtonStyle,
  ButtonBox,
} from 'components/styled/button/base-button'
import { ModalChildren } from 'components/styled/modal/modal-component'
import { RatingType, Rating } from './index'
import { RatingsList } from './ratings-list'

export interface MobileRatingProps {
  i18n: I18nComponentType
  dispatch?: any
  ownRating?: number
  average?: number
  onRate?: (rate: number) => void
  onResetRate?: () => void
  isLoading?: boolean
  ratings?: RatingType[]
  hideReviews?: boolean
}

const RatingModal = styled(Modal)`
  ${ModalChildren} {
    padding-left: ${({ theme }) => theme.space.s40};
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    ${ModalChildren} {
      padding-left: ${({ theme }) => theme.space.s16};
    }
  }
`

interface MobileRatingButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $variant?: string;
  $hasText?: boolean;
  $loading?: boolean;
  $size?: 'small' | 'default';
}

export const MobileRatingButton = styled.button.attrs<
  MobileRatingButtonProps
>((props) => ({
  $variant: 'secondary',
  $hasText: true,
  $loading: false,
  $size: 'default',
}))<MobileRatingButtonProps>`
  ${BaseButtonStyle};
  padding-top: ${({ theme }) => theme.space.s2};
  padding-bottom: ${({ theme }) => theme.space.s2};
  border-radius: ${({ theme }) => theme.space.s32};
`;

const ButtonContent = styled.div`
  min-height: ${({ theme }) => theme.space.s40};
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const Ratings = styled.div`
  display: flex;
  align-items: center;

  ${RatingStar} {
    cursor: inherit;
  }
`

const StyledHeader = styled(Subtitle)`
  padding-left: ${({ theme }) => theme.space.s8};

  ${({ theme }) => theme.mediaQuery.phone} {
    padding-left: ${({ theme }) => theme.space.s0};
  }
`

const RatingsListContainer = styled(Box)`
  padding-left: ${({ theme }) => theme.space.s8};

  ${({ theme }) => theme.mediaQuery.phone} {
    padding-left: ${({ theme }) => theme.space.s0};
  }
`

export const Average = styled.div`
  ${BodyStyle};
  font-weight: 600;
  margin-bottom: 0;
  word-break: normal;
  margin-left: ${({ theme }) => theme.space.s8};
`

const RatingLoadingIndicator = styled(LoadingIndicator)`
  margin: 0;
`

const VisiblyHiddenLabel = styled.span`
  ${VisiblyHidden}
`

export const OwnRating = styled(Small)`
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.color.grey90};
`

const MobileRatingFC: React.FC<MobileRatingProps> = ({
  i18n,
  ownRating,
  average,
  onRate,
  onResetRate,
  ratings,
  isLoading = false,
  hideReviews = false,
  dispatch,
  ...props
}) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const hasOwnRating = !!ownRating
  const showAverage = !!(!hideReviews || ownRating)

  const ratingModal = (
    <RatingModal
      id="mobile-rating-modal"
      onClose={() => setModalIsOpen(false)}
      isOpen={modalIsOpen}
      size="xSmall"
      primaryButton={{
        text: i18n.modalAlert.close,
      }}
      closeOnPrimaryClick
    >
      <StyledHeader mb="s16">{i18n.rating.mobileModalHeader}</StyledHeader>
      <Rating
        size="large"
        ownRating={ownRating}
        average={average}
        onRate={onRate}
        isLoading={isLoading}
        hideReviews={hideReviews}
      />
      {hasOwnRating && onResetRate && (
        <Box mt="s16" mb="s4">
          <Button
            variant="secondary"
            icon="undo"
            disabled={isLoading}
            onClick={() => onResetRate()}
            text={i18n.rating.reset}
          />
        </Box>
      )}
      {typeof ratings !== 'undefined' && showAverage && (
        <RatingsListContainer mt="s12">
          <RatingsList ratings={ratings} />
        </RatingsListContainer>
      )}
    </RatingModal>
  )

  return (
    <ButtonBox {...props}>
      <MobileRatingButton
        disabled={isLoading}
        onClick={() => setModalIsOpen(true)}
      >
        <ButtonContent>
          <Ratings>
            <RatingStars
              ownRating={ownRating}
              average={average}
              size="xSmall"
              isLoading={isLoading}
              showAverage={showAverage}
            />
            {!isLoading && showAverage ? (
              <Average>
                <VisiblyHiddenLabel>
                  {i18n.rating.averageRating}:
                </VisiblyHiddenLabel>
                {(average || 0).toFixed(1)}
              </Average>
            ) : (
              <>
                {isLoading ? (
                  <Box ml="s8">
                    <RatingLoadingIndicator color="primary30" size="s12" />
                  </Box>
                ) : null}
              </>
            )}
          </Ratings>
          {!!ownRating && (
            <OwnRating>
              <span>{`${i18n.rating.ownVote}: ${ownRating.toFixed(1)} `} </span>
            </OwnRating>
          )}
        </ButtonContent>
      </MobileRatingButton>
      {ratingModal}
    </ButtonBox>
  )
}

export const MobileRating = connect<{
  i18n: I18nComponentType
}>(mapLanguageToComponentProps())(MobileRatingFC)
